import {FileSpreadsheet, Inbox, PanelLeft, Settings} from 'lucide-react';
import {Link, useLocation} from 'react-router-dom';
import {cn} from '@/matic/lib/utils/cn';
import {env} from '@/matic/config/env';
import {useTriageCount} from '@/matic/features/relay/hooks/useTriageCount';
import {RELAY_CONFIG_PATH} from '@/matic/features/relay/components/config/relayConfigSections';
import {useSidebarStore} from './sidebarStore';
import RelayConfigSidebarNav from './RelayConfigSidebarNav';
import UserProfile from '../UserProfile';

/** One quoting navigation for both engine workspaces and both rail widths. */
export function QuotingSidebar({collapsed}: {collapsed: boolean}) {
  const {pathname} = useLocation();
  const openSidebar = useSidebarStore((state) => state.open);
  const toggle = useSidebarStore((state) => state.toggle);
  const inboxCount = useTriageCount();
  const inSettings = pathname.startsWith(RELAY_CONFIG_PATH);
  const items = [
    {label: 'Inbox', to: '/inbox', icon: Inbox, count: inboxCount},
    {label: 'Quotes', to: '/quotes', icon: FileSpreadsheet, count: 0},
    {label: 'Settings', to: RELAY_CONFIG_PATH, icon: Settings, count: 0},
  ];

  return (
    <div className="flex h-full flex-col bg-sidebar-bg text-sidebar-fg">
      <div
        className={cn(
          'flex h-16 shrink-0 items-center border-b border-sidebar-border',
          collapsed ? 'justify-center' : 'gap-2 px-4'
        )}
      >
        {!collapsed && (
          <Link
            to="/quotes"
            aria-label="Go to Quotes"
            className="min-w-0 flex-1 truncate text-xl font-bold text-white no-underline"
          >
            {env.APP_NAME}
          </Link>
        )}
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title="Toggle sidebar (Ctrl+B)"
          className="rounded p-2 hover:bg-sidebar-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        >
          <PanelLeft className="h-5 w-5" />
        </button>
      </div>
      <nav
        aria-label="Main navigation"
        className={cn(
          'flex flex-col gap-1 py-4',
          collapsed ? 'items-center' : 'px-3'
        )}
      >
        {items.map(({label, to, icon: Icon, count}) => {
          const active = pathname === to || pathname.startsWith(`${to}/`);
          return (
            <Link
              key={to}
              to={to}
              onClick={() => {
                if (to === RELAY_CONFIG_PATH) openSidebar();
              }}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              title={collapsed ? label : undefined}
              className={cn(
                'relative flex h-10 items-center rounded-md text-sm font-medium no-underline transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
                collapsed ? 'w-10 justify-center' : 'gap-3 px-3',
                active
                  ? 'bg-sidebar-active text-white'
                  : 'text-sidebar-fg/75 hover:bg-sidebar-hover hover:text-white'
              )}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span className="flex-1">{label}</span>}
              {count > 0 && (
                <span
                  className={cn(
                    'rounded-full bg-white/15 px-1.5 text-[10px] text-white',
                    collapsed && 'absolute -right-1 -top-1'
                  )}
                >
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      {!collapsed && inSettings && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <RelayConfigSidebarNav />
        </div>
      )}
      <div className="mt-auto border-t border-sidebar-border px-3 py-2">
        <UserProfile variant="sidebar" collapsed={collapsed} />
      </div>
    </div>
  );
}
